/**
 * EmployeeQuickCreateDialog - Minimal dialog for quick employee creation
 *
 * Following the Quick Create Dialog pattern:
 * - 3-5 fields maximum (First Name, Last Name, Email, Department)
 * - Redirects to detail page after creation for full editing
 * - Note: Compensation fields are financial and should be added on the detail page
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../shared/context';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { format } from 'date-fns';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { CompanyPicker, DialogForm, ValidatedEmailInput } from '../../../shared/components/common';
import { DepartmentPicker } from './DepartmentPicker';
import { useCreateEmployee } from '../hooks/useEmployees';
import type { EmployeeResponse } from '../../../generated/api/models/employeeResponse';

interface EmployeeQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Callback when employee is created (before navigation) */
  onCreated?: (employee: EmployeeResponse) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
}

interface QuickCreateFormData {
  first_name: string;
  last_name: string;
  email: string;
  department_id?: string;
  company_id: string | null;
}

export function EmployeeQuickCreateDialog({
  open,
  onClose,
  onCreated,
  skipNavigation = false,
}: EmployeeQuickCreateDialogProps) {
  const { t } = useTranslation(['people', 'common']);
  const { showWarning } = useNotification();
  const navigate = useNavigate();
  const createMutation = useCreateEmployee();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      department_id: undefined,
      company_id: null,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        first_name: '',
        last_name: '',
        email: '',
        department_id: undefined,
        company_id: null,
      });
    }
  }, [open, reset]);

  const onSubmit = async (data: QuickCreateFormData) => {
    const createData = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      hire_date: format(new Date(), 'yyyy-MM-dd'),
      employment_type: 'full_time' as const,
      department_id: data.department_id || undefined,
      company_id: data.company_id || undefined,
    };

    try {
      const newEmployee = await createMutation.mutateAsync({ data: createData });
      // Same contract as EmployeeForm: on a tenant that gives every employee a
      // login, a create that could not invite must say so — this dialog often
      // navigates straight to the new record, so silence here is the easiest
      // place to miss it.
      if (newEmployee?.user_invitation_blocked_reason) {
        showWarning(t('employees.inviteBlockedBody'));
      }
      onCreated?.(newEmployee);
      onClose();

      if (!skipNavigation) {
        navigate(`/people/employees/${newEmployee.id}`);
      }
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('employees.createTitle')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <Controller
          name="company_id"
          control={control}
          render={({ field }) => (
            <CompanyPicker
              value={field.value}
              onChange={field.onChange}
              label={t('common:multiCompany.employerOfRecord', 'Employer of record')}
              required
            />
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="first_name" required>{t('common:app.firstName')}</Label>
            <Controller
              name="first_name"
              control={control}
              rules={{ required: t('employees.firstNameRequired') }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="first_name"
                  autoFocus
                  className={cn(errors.first_name && 'border-destructive')}
                />
              )}
            />
            {errors.first_name && (
              <p className="text-sm text-destructive">{errors.first_name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="last_name" required>{t('common:app.lastName')}</Label>
            <Controller
              name="last_name"
              control={control}
              rules={{ required: t('employees.lastNameRequired') }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="last_name"
                  className={cn(errors.last_name && 'border-destructive')}
                />
              )}
            />
            {errors.last_name && (
              <p className="text-sm text-destructive">{errors.last_name.message}</p>
            )}
          </div>
        </div>

        <ValidatedEmailInput
          name="email"
          control={control}
          label={t('common:app.email')}
          required
        />

        <div className="space-y-2">
          {/* Quick Create is the "add my first employee" path, so it is where a
              fresh tenant most needs to be able to create a department rather
              than meet a dropdown offering only "None" (WI 39f6ce30). */}
          <Controller
            name="department_id"
            control={control}
            render={({ field }) => (
              <DepartmentPicker
                value={field.value ?? ''}
                onChange={(v) => field.onChange(v || undefined)}
                label={t('employees.department')}
              />
            )}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          {t('employees.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
